import type { FilterFieldDef } from './types'
import type { FilterOption } from './select-filter'
import { twJoin } from '@/lib/utils'

import { m } from '@/lib/paraglide/messages'

/** Giá trị "không lọc": chọn đủ mọi option, hoặc không chọn gì. */
export const MULTI_CHOICE_ALL = 'all'

const CHIP_LIST_LIMIT = 3

interface MultiChoiceFilterOptions {
    label: () => string
    options: FilterOption<string>[] | (() => FilterOption<string>[])
    /** Tập chọn khi URL không có tham số. Rỗng = không lọc. */
    defaultValues: readonly string[]
    /**
     * Nhận cả mã chưa khai trong `options` — link dashboard có thể mang mã thô
     * backend mới thêm; vứt đi thì màn mở lên KHÔNG lọc gì.
     */
    acceptUnknown?: (raw: string) => boolean
    /**
     * `checkboxes`: danh sách tick hiện sẵn (trạng thái, kiểu KiotViet) —
     * `all` = tick hết, không cho bỏ tích ô cuối. `chips`: nút bấm lọc theo —
     * `all` = không nút nào nhấn; nhấn đủ mọi nút vẫn giữ tường minh.
     */
    variant?: 'checkboxes' | 'chips'
}

/**
 * Bộ lọc chọn nhiều giá trị. Giá trị là chuỗi đã chuẩn hóa (theo thứ tự option,
 * mã lạ xếp sau) để core so sánh `===` với mặc định được và ghi thẳng lên URL
 * dạng `a,b,c` — cùng dạng API nhận.
 */
export function multiChoiceFilter({
    label,
    options,
    defaultValues,
    acceptUnknown,
    variant = 'checkboxes',
}: MultiChoiceFilterOptions): FilterFieldDef<string> {
    const resolve = () => (typeof options === 'function' ? options() : options)

    const canonical = (values: readonly string[]) => {
        const known = resolve().map((o) => o.value)
        const picked = new Set(values)
        const inOrder = known.filter((v) => picked.has(v))
        const unknown = [...picked].filter((v) => !known.includes(v)).sort()
        if (inOrder.length + unknown.length === 0) return MULTI_CHOICE_ALL
        if (
            variant === 'checkboxes' &&
            unknown.length === 0 &&
            inOrder.length === known.length
        )
            return MULTI_CHOICE_ALL
        return [...inOrder, ...unknown].join(',')
    }

    const selectedOf = (value: string) =>
        value === MULTI_CHOICE_ALL
            ? variant === 'checkboxes'
                ? resolve().map((o) => o.value)
                : []
            : value.split(',')

    const labelOf = (value: string) =>
        resolve().find((o) => o.value === value)?.label ?? value

    // Bỏ một trạng thái mà chip liệt kê chín nhãn còn lại thì không ai đọc
    // nổi: quá ba nhãn thì ghi phần bị bỏ, hoặc chỉ ghi số lượng.
    const summarize = (value: string) => {
        if (value === MULTI_CHOICE_ALL) return m.common_all()
        const picked = value.split(',')
        if (picked.length <= CHIP_LIST_LIMIT)
            return picked.map(labelOf).join(', ')
        const known = resolve()
        const missing = known.filter((o) => !picked.includes(o.value))
        const onlyKnown = picked.every((v) => known.some((o) => o.value === v))
        if (onlyKnown && missing.length === 0) return m.common_all()
        if (onlyKnown && missing.length <= CHIP_LIST_LIMIT)
            return m.list_filter_all_except({
                labels: missing.map((o) => o.label).join(', '),
            })
        return m.list_filter_selected_count({ count: String(picked.length) })
    }

    return {
        defaultValue: canonical(defaultValues),
        label,
        parse: (raw) => {
            if (typeof raw !== 'string') return undefined
            if (raw === MULTI_CHOICE_ALL) return MULTI_CHOICE_ALL
            const known = resolve().map((o) => o.value)
            const values = raw
                .split(',')
                .map((v) => v.trim())
                .filter(
                    (v) =>
                        v !== '' &&
                        (known.includes(v) || (acceptUnknown?.(v) ?? false)),
                )
            return values.length === 0 ? undefined : canonical(values)
        },
        chipLabel: (value) => `${label()}: ${summarize(value)}`,
        render: ({ id, value, onChange, surface }) => {
            const selected = selectedOf(value)
            const toggle = (option: string) => {
                const next = selected.includes(option)
                    ? selected.filter((v) => v !== option)
                    : [...selected, option]
                if (variant === 'checkboxes' && next.length === 0) return
                onChange(canonical(next))
            }
            // Mã lạ từ deep-link vẫn phải hiện để người dùng thấy và bỏ được.
            const shown = [
                ...resolve(),
                ...selected
                    .filter((v) => !resolve().some((o) => o.value === v))
                    .map((v) => ({ value: v, label: v })),
            ]

            if (variant === 'chips') {
                return (
                    <div id={id} role="group" className="flex flex-wrap gap-2">
                        {shown.map((opt) => {
                            const on = selected.includes(opt.value)
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    aria-pressed={on}
                                    onClick={() => toggle(opt.value)}
                                    className={twJoin(
                                        'rounded-full border px-3 text-sm transition-colors',
                                        'focus-visible:outline-ring focus-visible:outline-2',
                                        surface === 'sheet' ? 'h-11' : 'h-8',
                                        on
                                            ? 'border-primary bg-primary text-primary-foreground'
                                            : 'bg-background hover:bg-muted',
                                    )}
                                >
                                    {opt.label}
                                </button>
                            )
                        })}
                    </div>
                )
            }

            return (
                <div id={id} role="group" className="grid gap-1">
                    {shown.map((opt) => (
                        <label
                            key={opt.value}
                            className={twJoin(
                                'flex cursor-pointer items-center gap-2 text-sm',
                                surface === 'sheet' ? 'min-h-11' : 'min-h-7',
                            )}
                        >
                            <input
                                type="checkbox"
                                className="accent-primary size-4"
                                checked={selected.includes(opt.value)}
                                onChange={() => toggle(opt.value)}
                            />
                            {opt.label}
                        </label>
                    ))}
                </div>
            )
        },
    }
}
